import { LocationFeatureCreditDetails } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { CustomizationFlagTypes } from '@frontend/api-customization-flags';

export type LocationCreditStatus = {
  locationId: string;
  locationName: string;
};

export type AiReceptionistCreditStatus = {
  outOfCreditsLocations: LocationCreditStatus[];
  notSubscribedLocations: LocationCreditStatus[];
};

/**
 * Determines AI Receptionist credit/subscription status for relevant locations.
 *
 * - Not subscribed: location does not have TRUELARK_AI_VOICE customization flag ACTIVE
 * - Out of credits: consumedCredits >= totalCredits (only for subscribed locations)
 */
export function getAiReceptionistCreditStatus({
  locationCreditDetails,
  locationIds,
  getScopeName,
  aiVoiceFlagLocationMap,
}: {
  locationCreditDetails: Record<string, LocationFeatureCreditDetails>;
  locationIds: string[];
  getScopeName: (id: string) => string;
  aiVoiceFlagLocationMap: Record<string, { state?: string } | undefined>;
}): AiReceptionistCreditStatus {
  const outOfCreditsLocations: LocationCreditStatus[] = [];
  const notSubscribedLocations: LocationCreditStatus[] = [];

  for (const locationId of locationIds) {
    const flagEntry = aiVoiceFlagLocationMap[locationId];
    const isSubscribed = flagEntry?.state === CustomizationFlagTypes.CustomizationFlagState.ACTIVE;

    if (!isSubscribed) {
      notSubscribedLocations.push({
        locationId,
        locationName: getScopeName(locationId),
      });
    } else {
      const details = locationCreditDetails[locationId];
      const totalCredits = details?.credits?.totalCredits;
      const consumedCredits = details?.credits?.consumedCredits;

      if (consumedCredits !== undefined && totalCredits !== undefined && consumedCredits >= totalCredits) {
        outOfCreditsLocations.push({
          locationId,
          locationName: getScopeName(locationId),
        });
      }
    }
  }

  return { outOfCreditsLocations, notSubscribedLocations };
}

/**
 * Derives the unique set of phone number locationIds relevant for credit checks.
 * If the call route has phone numbers assigned, use the unique locationIds from those phone numbers.
 * If not, fall back to all unique locationIds from all available phone numbers.
 */
export function getRelevantLocationIds({
  callRoutePhoneNumbers,
  allPhoneNumbers,
}: {
  callRoutePhoneNumbers: { locationId?: string }[];
  allPhoneNumbers: { locationId?: string }[];
}): string[] {
  const phoneNumbers = callRoutePhoneNumbers.length > 0 ? callRoutePhoneNumbers : allPhoneNumbers;
  const locationIds = new Set<string>();

  for (const pn of phoneNumbers) {
    if (pn.locationId) {
      locationIds.add(pn.locationId);
    }
  }

  return Array.from(locationIds);
}
